import type { User, CellData } from '@flipboard/shared';
import { config } from './config';

// API base URL - environment-aware based on hostname
const API_BASE = config.apiUrl;

export interface Board {
  id: string;
  name: string | null;
  rows: number;
  cols: number;
  isOwner?: boolean;
  ownerTier?: string;
  fillScreen?: boolean;
  createdAt: number;
  updatedAt: number;
  viewers?: number;
}

/**
 * Build headers for authenticated requests
 */
function authHeaders(token: string | null): HeadersInit {
  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Get current user from session
 */
export async function getCurrentUser(token: string | null): Promise<User | null> {
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    return null;
  }

  const data = await res.json();
  return data.user;
}

/**
 * Get the Google OAuth login URL
 */
export function getGoogleLoginUrl(): string {
  return `${API_BASE}/api/auth/google`;
}

/**
 * Logout the current user
 */
export async function logout(token: string | null): Promise<void> {
  await fetch(`${API_BASE}/api/auth/logout`, {
    method: 'POST',
    headers: authHeaders(token),
  });
}

/**
 * Get user's boards
 */
export async function getBoards(token: string | null): Promise<Board[]> {
  const res = await fetch(`${API_BASE}/api/boards`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to fetch boards');
  }

  const data = await res.json();
  return data.boards;
}

/**
 * Create a new board
 */
export async function createBoard(
  name?: string,
  rows = 6,
  cols = 22,
  token: string | null = null
): Promise<Board> {
  const res = await fetch(`${API_BASE}/api/boards`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, rows, cols }),
  });

  if (!res.ok) {
    throw new Error('Failed to create board');
  }

  const data = await res.json();
  return data.board;
}

/**
 * Get a single board
 */
export async function getBoard(boardId: string, token: string | null = null): Promise<Board> {
  const res = await fetch(`${API_BASE}/api/boards/${boardId}`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to fetch board');
  }

  const data = await res.json();
  return data.board;
}

/**
 * Get board state (including current cell content)
 */
export async function getBoardState(
  boardId: string,
  token: string | null = null
): Promise<{ cells: CellData[][]; rows: number; cols: number }> {
  const res = await fetch(`${API_BASE}/api/boards/${boardId}/state`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to fetch board state');
  }

  const data = await res.json();
  return data.state;
}

/**
 * Get viewer count for a board
 */
export async function getViewerCount(
  boardId: string,
  token: string | null = null
): Promise<number> {
  const res = await fetch(`${API_BASE}/api/boards/${boardId}/viewers`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    return 0;
  }

  const data = await res.json();
  return data.viewers || 0;
}

/**
 * Send a message to a board
 */
export async function sendMessage(
  boardId: string,
  text: string,
  clear = false,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/boards/${boardId}/message`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text, clear }),
  });

  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || 'Failed to send message');
  }
}

/**
 * Update a board
 */
export async function updateBoard(
  boardId: string,
  updates: {
    name?: string;
    rows?: number;
    cols?: number;
    fillScreen?: boolean;
  },
  token: string | null = null
): Promise<Board> {
  const res = await fetch(`${API_BASE}/api/boards/${boardId}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!res.ok) {
    throw new Error('Failed to update board');
  }

  const data = await res.json();
  return data.board;
}

/**
 * Delete a board
 */
export async function deleteBoard(boardId: string, token: string | null = null): Promise<void> {
  const res = await fetch(`${API_BASE}/api/boards/${boardId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to delete board');
  }
}

/**
 * Toggle sound on displays
 */
export async function toggleSound(
  boardId: string,
  enabled: boolean,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/boards/${boardId}/sound`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ enabled }),
  });

  if (!res.ok) {
    throw new Error('Failed to toggle sound');
  }
}

/**
 * Get the display URL for a board
 */
export function getDisplayUrl(boardId: string): string {
  return `${config.displayUrl}/b/${boardId}`;
}

/**
 * API Key types
 */
export interface ApiKey {
  id: string;
  prefix: string;
  name: string;
  scopes: string[];
  lastUsedAt: number | null;
  createdAt: number;
}

export interface CreateApiKeyResponse {
  apiKey: ApiKey & { key: string };
}

/**
 * Get user's API keys
 */
export async function getApiKeys(token: string | null): Promise<ApiKey[]> {
  const res = await fetch(`${API_BASE}/api/api-keys`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to fetch API keys');
  }

  const data = await res.json();
  return data.apiKeys;
}

/**
 * Create a new API key
 */
export async function createApiKey(
  name: string,
  scopes: string[],
  token: string | null = null
): Promise<CreateApiKeyResponse> {
  const res = await fetch(`${API_BASE}/api/api-keys`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, scopes }),
  });

  if (!res.ok) {
    throw new Error('Failed to create API key');
  }

  return await res.json();
}

/**
 * Delete an API key
 */
export async function deleteApiKey(
  keyId: string,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/api-keys/${keyId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to delete API key');
  }
}

/**
 * Subscription types
 */
export interface CheckoutSessionResponse {
  checkoutUrl: string;
}

export interface PortalSessionResponse {
  portalUrl: string;
}

/**
 * Create Stripe checkout session for subscription upgrade
 */
export async function createCheckoutSession(
  tier: 'pro' | 'commercial',
  billingCycle: 'monthly' | 'annual',
  token: string | null = null
): Promise<CheckoutSessionResponse> {
  const res = await fetch(`${API_BASE}/api/subscription/checkout`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tier, billingCycle }),
  });

  if (!res.ok) {
    throw new Error('Failed to create checkout session');
  }

  return await res.json();
}

/**
 * Create Stripe customer portal session for managing subscription
 */
export async function createPortalSession(
  token: string | null = null
): Promise<PortalSessionResponse> {
  const res = await fetch(`${API_BASE}/api/subscription/portal`, {
    method: 'POST',
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to create portal session');
  }

  return await res.json();
}

/**
 * Admin API types and functions
 */
export interface AdminMetrics {
  users: number;
  boards: number;
  viewers: number;
}

export interface AdminUser extends User {
  boardCount: number;
}

export interface AdminUsersResponse {
  users: AdminUser[];
}

/**
 * Get admin metrics (admin only)
 */
export async function getAdminMetrics(
  token: string | null = null
): Promise<AdminMetrics> {
  const res = await fetch(`${API_BASE}/api/admin/metrics`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to fetch admin metrics');
  }

  return await res.json();
}

/**
 * Get all users (admin only)
 */
export async function getAdminUsers(
  token: string | null = null
): Promise<AdminUsersResponse> {
  const res = await fetch(`${API_BASE}/api/admin/users`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to fetch admin users');
  }

  return await res.json();
}

/**
 * Admin user detail types
 */
export interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    name: string | null;
    avatarUrl: string | null;
    createdAt: number;
    isAdmin: boolean;
    subscriptionTier: string;
    subscriptionStatus: string;
    subscriptionPeriodEnd: number | null;
    billingCycle: string | null;
    stripeCustomerId: string | null;
  };
  boards: Array<{
    id: string;
    name: string;
    rows: number;
    cols: number;
    isPublic: boolean;
    createdAt: number;
    updatedAt: number;
  }>;
  apiKeys: Array<{
    id: string;
    name: string;
    keyPrefix: string;
    scopes: string[];
    lastUsedAt: number | null;
    createdAt: number;
  }>;
}

/**
 * Get detailed user information (admin only)
 */
export async function getAdminUserDetail(
  userId: string,
  token: string | null = null
): Promise<AdminUserDetail> {
  const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to fetch user details');
  }

  return await res.json();
}

/**
 * Update user subscription (admin only)
 */
export async function updateAdminUserSubscription(
  userId: string,
  updates: {
    subscriptionTier?: string;
    subscriptionStatus?: string;
    subscriptionPeriodEnd?: number | null;
  },
  token: string | null = null
): Promise<{ user: AdminUserDetail['user'] }> {
  const res = await fetch(`${API_BASE}/api/admin/users/${userId}/subscription`, {
    method: 'PATCH',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });

  if (!res.ok) {
    throw new Error('Failed to update subscription');
  }

  return await res.json();
}

/**
 * Delete user's board (admin only)
 */
export async function deleteAdminUserBoard(
  userId: string,
  boardId: string,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/users/${userId}/boards/${boardId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to delete board');
  }
}

/**
 * Delete user's API key (admin only)
 */
export async function deleteAdminUserApiKey(
  userId: string,
  keyId: string,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/users/${userId}/api-keys/${keyId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to delete API key');
  }
}

/**
 * Impersonate user (admin only)
 */
export async function impersonateUser(
  userId: string,
  token: string | null = null
): Promise<{ token: string; expiresAt: number; user: AdminUserDetail['user'] }> {
  const res = await fetch(`${API_BASE}/api/admin/users/${userId}/impersonate`, {
    method: 'POST',
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to impersonate user');
  }

  return await res.json();
}

/**
 * Delete user account (admin only)
 */
export async function deleteAdminUser(
  userId: string,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to delete user');
  }
}

// ============ Playlist API ============

export interface Playlist {
  id: string;
  name: string | null;
  rows: number | null;
  cols: number | null;
  fillScreen: boolean | null;
  soundEnabled: boolean | null;
  defaultDuration: number;
  shuffle: boolean;
  itemCount?: number;
  createdAt: number;
  updatedAt: number;
}

export interface PlaylistItem {
  id: string;
  playlistId: string;
  boardId: string;
  position: number;
  duration: number | null;
  createdAt: number;
  board?: {
    id: string;
    name: string | null;
    rows: number;
    cols: number;
  };
}

export interface PlaylistWithItems extends Playlist {
  items: PlaylistItem[];
}

/**
 * Get user's playlists
 */
export async function getPlaylists(token: string | null): Promise<Playlist[]> {
  const res = await fetch(`${API_BASE}/api/playlists`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to fetch playlists');
  }

  const data = await res.json();
  return data.playlists;
}

/**
 * Create a new playlist
 */
export async function createPlaylist(
  name?: string,
  defaultDuration = 10,
  shuffle = false,
  token: string | null = null
): Promise<Playlist> {
  const res = await fetch(`${API_BASE}/api/playlists`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, defaultDuration, shuffle }),
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to create playlist');
  }

  const data = await res.json();
  return data.playlist;
}

/**
 * Get a single playlist with items
 */
export async function getPlaylist(
  playlistId: string,
  token: string | null = null
): Promise<PlaylistWithItems> {
  const res = await fetch(`${API_BASE}/api/playlists/${playlistId}`, {
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to fetch playlist');
  }

  const data = await res.json();
  return data.playlist;
}

/**
 * Update playlist settings
 */
export async function updatePlaylist(
  playlistId: string,
  updates: Partial<Playlist>,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/playlists/${playlistId}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!res.ok) {
    throw new Error('Failed to update playlist');
  }
}

/**
 * Delete a playlist
 */
export async function deletePlaylist(
  playlistId: string,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/playlists/${playlistId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to delete playlist');
  }
}

/**
 * Add board to playlist
 */
export async function addPlaylistItem(
  playlistId: string,
  boardId: string,
  duration: number | null = null,
  token: string | null = null
): Promise<PlaylistItem> {
  const res = await fetch(`${API_BASE}/api/playlists/${playlistId}/items`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ boardId, duration }),
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to add board to playlist');
  }

  const data = await res.json();
  return data.item;
}

/**
 * Update playlist item duration
 */
export async function updatePlaylistItem(
  playlistId: string,
  itemId: string,
  duration: number | null,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/playlists/${playlistId}/items/${itemId}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ duration }),
  });

  if (!res.ok) {
    throw new Error('Failed to update playlist item');
  }
}

/**
 * Remove board from playlist
 */
export async function removePlaylistItem(
  playlistId: string,
  itemId: string,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/playlists/${playlistId}/items/${itemId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!res.ok) {
    throw new Error('Failed to remove board from playlist');
  }
}

/**
 * Reorder playlist items
 */
export async function reorderPlaylistItems(
  playlistId: string,
  order: Array<{ itemId: string; position: number }>,
  token: string | null = null
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/playlists/${playlistId}/reorder`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ order }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    console.error('Reorder API error:', res.status, errorData);
    throw new Error(`Failed to reorder playlist items: ${errorData.error || res.statusText}`);
  }
}

/**
 * Get playlist display URL
 */
export function getPlaylistDisplayUrl(playlistId: string): string {
  return `${config.displayUrl}/p/${playlistId}`;
}
